import type { UpdateSSOImageLangViewModel } from '@/shared/api/generated';
import type { SupportedDevice, SupportedLanguage, VariantKey } from '../store/atoms/slideAtom';

/**
 * Combinações idioma × viewport obrigatórias para considerar um slide completo.
 * Fonte de verdade única usada tanto na biblioteca quanto no editor.
 */
export const REQUIRED_VARIANTS: readonly {
  readonly cultureInfo: SupportedLanguage;
  readonly viewPort: 1 | 2;
  readonly device: SupportedDevice;
  readonly variantKey: VariantKey;
}[] = [
  { cultureInfo: 'pt-BR', viewPort: 1, device: 'desktop', variantKey: 'pt-BR-desktop' },
  { cultureInfo: 'pt-BR', viewPort: 2, device: 'mobile', variantKey: 'pt-BR-mobile' },
  { cultureInfo: 'en-US', viewPort: 1, device: 'desktop', variantKey: 'en-US-desktop' },
  { cultureInfo: 'en-US', viewPort: 2, device: 'mobile', variantKey: 'en-US-mobile' },
  { cultureInfo: 'es-ES', viewPort: 1, device: 'desktop', variantKey: 'es-ES-desktop' },
  { cultureInfo: 'es-ES', viewPort: 2, device: 'mobile', variantKey: 'es-ES-mobile' },
];

/**
 * Verifica se um slide da biblioteca está completo — todas as 6 variantes
 * (3 idiomas × 2 viewports) possuem `urlStorage` preenchido.
 *
 * Usada no `SlideCard` para habilitar o botão de publicar.
 *
 * @param langs - Array `ssoImageLangs` retornado pela API.
 * @returns `true` quando todas as 6 combinações obrigatórias têm conteúdo.
 */
export function isSlideComplete(langs: UpdateSSOImageLangViewModel[] | null | undefined): boolean {
  if (!langs || langs.length === 0) return false;
  return REQUIRED_VARIANTS.every(required =>
    langs.some(
      lang =>
        lang.cultureInfo === required.cultureInfo &&
        lang.viewPort === required.viewPort &&
        lang.file != null,
    ),
  );
}

/**
 * Verifica se o estado in-memory do editor está completo — todas as 6 variantes
 * têm `canvas` preenchido.
 *
 * Usada no `useSlideIO` para derivar `canSave`.
 *
 * @param variants - Mapa de variantes do slide ativo (`slide.variants`).
 * @returns `true` quando todas as 6 combinações obrigatórias têm canvas.
 */
export function isEditorSlideComplete(variants: Record<VariantKey, { canvas: unknown }>): boolean {
  return REQUIRED_VARIANTS.every(({ variantKey }) => variants[variantKey]?.canvas != null);
}
